import { useEffect, useRef, useState } from 'react'
import { Cpu, Droplets, Globe2, Mail, Menu, Plug, Radio, Settings2, Thermometer, X } from 'lucide-react'
import { languages, useI18n } from './i18n.jsx'
import Hero from './components/Hero.jsx'
import Solutions from './components/Solutions.jsx'
import ProductFamilies from './components/ProductFamilies.jsx'
import StructuralDesign from './components/StructuralDesign.jsx'
import HardwareDesign from './components/HardwareDesign.jsx'
import SoftwareAlgorithms from './components/SoftwareAlgorithms.jsx'
import FactorySection from './components/FactorySection.jsx'
import Footer from './components/Footer.jsx'

const email = 'marketing@rti-tek.com'

function Logo() {
  const { t } = useI18n()
  return <a href="#top" className="brand" aria-label={t('site.home')}>Rti-Tek</a>
}

function LanguageMenu() {
  const { language, setLanguage, t } = useI18n()
  return <label className="language-selector"><Globe2 size={17} aria-hidden="true" /><span className="sr-only">{t('site.language')}</span>
    <select value={language} onChange={(event) => setLanguage(event.target.value)} aria-label={t('site.language')}>
      {languages.map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}
    </select>
  </label>
}

export default function App() {
  const { t, language } = useI18n()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButton = useRef(null)
  const mailto = (subject = t('site.email_subject_sales'), product = '') => `mailto:${email}?subject=${encodeURIComponent(product ? `${subject} - ${product}` : subject)}&body=${encodeURIComponent(t('site.email_body'))}`
  const products = [
    { id: 'valve', title: t('site.valve_title'), description: t('site.valve_description'), icon: Thermometer, model: 'TRV901W', features: t('site.valve_features') },
    { id: 'panel', title: t('site.panel_title'), description: t('site.panel_description'), icon: Settings2 },
    { id: 'sensor', title: t('site.sensor_title'), description: t('site.sensor_description'), icon: Radio },
    { id: 'control', title: t('site.control_title'), description: t('site.control_description'), icon: Cpu },
    { id: 'plug', title: t('site.plug_title'), description: t('site.plug_description'), icon: Plug },
    { id: 'water', title: t('site.water_title'), description: t('site.water_description'), icon: Droplets },
  ]

  useEffect(() => {
    document.title = `Rti-Tek | ${t('site.meta_title')}`
    const meta = document.querySelector('meta[name="description"]') || document.head.appendChild(document.createElement('meta'))
    meta.name = 'description'
    meta.content = t('site.hero_description')
    setMenuOpen(false)
  }, [t, language])
  useEffect(() => {
    const hash = window.location.hash
    if (!hash) return
    let disposed = false, frame
    const restoreAnchor = () => {
      document.fonts.ready.then(() => {
        if (disposed) return
        frame = requestAnimationFrame(() => {
          if (disposed || window.location.hash !== hash) return
          let id
          try { id = decodeURIComponent(hash.slice(1)) } catch { return }
          document.getElementById(id)?.scrollIntoView({ behavior: 'instant', block: 'start' })
        })
      })
    }
    // The initial document is an empty React root, so native hash restoration can miss its target.
    if (document.readyState === 'complete') restoreAnchor()
    else window.addEventListener('load', restoreAnchor, { once: true })
    return () => {
      disposed = true
      cancelAnimationFrame(frame)
      window.removeEventListener('load', restoreAnchor)
    }
  }, [])
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && menuOpen) { setMenuOpen(false); menuButton.current?.focus() }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [menuOpen])

  return <div className="site">
    <header className="site-header"><div className="header-inner"><Logo />
      <nav id="site-navigation" aria-label={t('site.nav_products')} className={`site-nav ${menuOpen ? 'is-open' : ''}`}>
        <a href="#top" onClick={() => setMenuOpen(false)}>{t('site.nav_home')}</a>
        <a href="#product-families" onClick={() => setMenuOpen(false)}>{t('site.nav_product_series')}</a>
        <span className="site-nav-pending" aria-disabled="true">{t('site.nav_news')}</span>
        <a href="#factory" onClick={() => setMenuOpen(false)}>{t('site.nav_about')}</a>
      </nav>
      <div className="header-actions"><LanguageMenu /><a href="#contact" className="header-contact icon-button" title={t('site.contact_sales')} aria-label={t('site.contact_sales')}><Mail size={18} /></a>
        <button ref={menuButton} type="button" className="icon-button menu-toggle" aria-label={menuOpen ? t('site.close_menu') : t('site.menu')} aria-expanded={menuOpen} aria-controls="site-navigation" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X size={20} /> : <Menu size={20} />}</button>
      </div>
    </div></header>
    <main id="content">
      <Hero />

      <Solutions mailto={mailto} />

      <StructuralDesign />
      <HardwareDesign />

      <SoftwareAlgorithms />
      <FactorySection />
    </main>
    <ProductFamilies products={products} />
    <Footer />
  </div>
}
